import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Header from "@/pages/home/components/Header";
import Footer from "@/pages/home/components/Footer";
import { supabase } from "@/lib/supabase";

interface VerifiedCompany {
  id: number;
  name: string;
  website_url: string;
  registration_code: string;
  deposit_amount: string;
  game_types: string;
  features: string;
  content: string;
  image_url: string;
  category: string;
  is_active: boolean;
  sort_order: number;
  created_at: string;
}

export default function VerifiedPage() {
  const [companies, setCompanies] = useState<VerifiedCompany[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalDeposit, setTotalDeposit] = useState("");

  useEffect(() => {
    let cancelled = false;

    const fetchCompanies = async () => {
      try {
        setLoading(true);
        const { data, error } = await supabase
          .from("verified_companies")
          .select("*")
          .eq("is_active", true)
          .order("sort_order", { ascending: true });

        if (cancelled) return;
        if (error) throw error;

        setCompanies(data || []);

        const deposits = (data || []).reduce((sum, c) => {
          const amount = c.deposit_amount || "";
          const num = parseInt(amount.replace(/[^0-9]/g, ""), 10);
          return sum + (isNaN(num) ? 0 : num);
        }, 0);

        const formatted = deposits >= 10000
          ? `${(deposits / 10000).toLocaleString()}억원`
          : `${deposits.toLocaleString()}만원`;
        setTotalDeposit(formatted);
      } catch {
        if (!cancelled) setCompanies([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchCompanies();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="min-h-screen bg-background-50">
      <Header />

      {/* Page Header */}
      <section className="bg-background-100 border-b border-background-300">
        <div className="max-w-[1000px] mx-auto px-3 md:px-4 py-8 md:py-10">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-sm text-foreground-600 hover:text-primary-400 transition-colors mb-3"
          >
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-left-line" />
            </span>
            홈으로
          </Link>
          <div className="flex items-center gap-3">
            <span className="w-9 h-9 flex items-center justify-center text-primary-500">
              <i className="ri-verified-badge-line text-2xl" />
            </span>
            <div>
              <h1 className="text-xl md:text-2xl font-bold text-foreground-700">보증업체</h1>
              <p className="text-sm text-foreground-600 mt-0.5">
                엄격한 검증을 거쳐 선정한 믿을 수 있는 업체들입니다
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Summary */}
      <section className="max-w-[1000px] mx-auto px-3 md:px-4 py-6">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="bg-background-100 rounded-lg border border-background-300 p-4 text-center">
            <div className="text-sm text-foreground-600 mb-1">총 보증금액</div>
            <div className="text-xl md:text-2xl font-black text-primary-500">
              {totalDeposit || "-"}
            </div>
          </div>
          <div className="bg-background-100 rounded-lg border border-background-300 p-4 text-center">
            <div className="text-sm text-foreground-600 mb-1">보증업체 수</div>
            <div className="text-xl md:text-2xl font-black text-accent-500">{companies.length}개</div>
          </div>
          <div className="bg-background-100 rounded-lg border border-background-300 p-4 text-center">
            <div className="text-sm text-foreground-600 mb-1">업데이트</div>
            <div className="text-xl md:text-2xl font-black text-secondary-500">실시간</div>
          </div>
        </div>
      </section>

      {/* Loading */}
      {loading && (
        <section className="max-w-[1000px] mx-auto px-3 md:px-4 pb-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="bg-background-100 rounded-lg border border-background-300 p-5 animate-pulse">
                <div className="flex gap-4">
                  <div className="w-16 h-16 rounded-lg bg-background-200 flex-shrink-0" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-background-200 rounded w-2/3" />
                    <div className="h-3 bg-background-200 rounded w-1/2" />
                    <div className="h-3 bg-background-200 rounded w-1/3" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Empty */}
      {!loading && companies.length === 0 && (
        <section className="max-w-[1000px] mx-auto px-3 md:px-4 pb-12">
          <div className="text-center py-20">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-100 border border-background-300 flex items-center justify-center">
              <span className="w-8 h-8 flex items-center justify-center text-foreground-600">
                <i className="ri-shield-check-line text-2xl" />
              </span>
            </div>
            <p className="text-base text-foreground-600">등록된 보증업체가 없습니다</p>
          </div>
        </section>
      )}

      {/* Companies List */}
      {!loading && companies.length > 0 && (
        <section className="max-w-[1000px] mx-auto px-3 md:px-4 pb-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {companies.map((company) => (
              <Link
                key={company.id}
                to={`/verified/${company.id}`}
                className="bg-background-100 rounded-lg border border-background-300 p-5 hover:border-primary-500/30 transition-colors block"
              >
                <div className="flex items-start gap-4">
                  {/* Thumbnail */}
                  <div className="w-16 h-16 rounded-lg overflow-hidden bg-background-200 border border-background-300 flex items-center justify-center flex-shrink-0">
                    {company.image_url ? (
                      <img
                        src={company.image_url}
                        alt={company.name}
                        className="w-full h-full object-contain"
                      />
                    ) : (
                      <span className="w-7 h-7 flex items-center justify-center text-foreground-600">
                        <i className="ri-building-line text-xl" />
                      </span>
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    {/* Name + Verified Badge */}
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-base font-bold text-foreground-700">{company.name}</h3>
                      <span className="w-4 h-4 flex items-center justify-center text-primary-500">
                        <i className="ri-verified-badge-fill" />
                      </span>
                    </div>

                    {/* Domain */}
                    {company.website_url && (
                      <span className="text-xs text-primary-500 block mb-2 truncate">
                        {company.website_url}
                      </span>
                    )}

                    {/* Meta info */}
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-foreground-600 mb-2">
                      {company.deposit_amount && (
                        <span className="font-medium text-foreground-700">보증금 {company.deposit_amount}</span>
                      )}
                      {company.registration_code && (
                        <span className="font-mono bg-background-200 px-2 py-0.5 rounded text-[11px]">
                          가입코드 {company.registration_code}
                        </span>
                      )}
                      {company.category && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-secondary-100 text-secondary-900">
                          {company.category}
                        </span>
                      )}
                    </div>

                    {/* Game Types */}
                    {company.game_types && (
                      <div className="flex flex-wrap gap-1 mb-2">
                        {company.game_types.split(",").map((gt) => (
                          <span
                            key={gt}
                            className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] bg-background-200 text-foreground-600 whitespace-nowrap"
                          >
                            {gt.trim()}
                          </span>
                        ))}
                      </div>
                    )}

                    {/* Features */}
                    {company.features && (
                      <p className="text-xs text-foreground-600 line-clamp-2">{company.features}</p>
                    )}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
}